import {
	Box,
	FormControl,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	Tooltip,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactComponent as Arrow } from 'assets/icons/nav_arrow_down.svg'
import { useGetMilkersInfo } from 'hook/query/useGetMilkersInfo'
import { useGetMilkingParlorNames } from 'hook/query/useGetMilkingParlorNames'
import { useGetParlorZones } from 'hook/query/useGetParlorZones'
import { useParlorAvailableShiftPeriods } from 'hook/query/useParlorAvailableShiftPeriods'
import { ShiftOptions } from 'models/ICattlesData'
import { IMilker } from 'models/MilkingProcess'
import moment, { Moment } from 'moment'
import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import MilkingProcessService from 'services/milkingProcessService'
import { useAppDispatch } from 'store/hooks'
import { notifyUser } from 'store/notificationsSlice'
import { COLORS } from 'styles/colors'
import { ButtonUI } from 'ui/buttons/ButtonUI'
import DatePicker from 'ui/DatePickerInput/DatePicker'
import { DateRestriction } from 'ui/DatePickerInput/types'
import Drawer from 'ui/Drawer'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographyPrimary14Bold } from 'ui/typography/TypographyPrimary14Bold'

interface AssignShiftDrawerProps {
	open: boolean
	onClose: () => void
	initialValues?: AssignShiftInitialValues | null
}

export interface AssignShiftInitialValues {
	milkerId?: number
	workDate?: number
	parlorId?: number | null
	period?: ShiftOptions | null
}

const SHIFT_PERIOD_ORDER: ShiftOptions[] = [
	ShiftOptions.AM,
	ShiftOptions.PM,
	ShiftOptions.NIGHT,
]

const SHIFT_PERIOD_LABELS: Record<ShiftOptions, string> = {
	[ShiftOptions.AM]: 'Утренняя смена',
	[ShiftOptions.PM]: 'Дневная смена',
	[ShiftOptions.NIGHT]: 'Ночная смена',
}

interface OptionChipProps {
	label: string
	selected?: boolean
	disabled?: boolean
	onClick?: () => void
}

const OptionChip: FC<OptionChipProps> = ({
	label,
	selected = false,
	disabled = false,
	onClick,
}) => {
	return (
		<Box
			component='button'
			type='button'
			disabled={disabled}
			onClick={onClick}
			sx={{
				height: '30px',
				px: '12px',
				borderRadius: '10px',
				border: `1px solid ${selected ? COLORS.main : '#E2E2E2'}`,
				backgroundColor: selected ? COLORS.main : '#FFFFFF',
				color: selected ? '#FFFFFF' : COLORS.black,
				fontSize: '14px',
				fontWeight: 400,
				cursor: disabled ? 'default' : 'pointer',
				opacity: disabled ? 0.5 : 1,
			}}
		>
			{label}
		</Box>
	)
}

interface PlaceChipProps {
	label: string
	selected?: boolean
	disabled?: boolean
	onClick?: () => void
}

const PlaceChip: FC<PlaceChipProps> = ({
	label,
	selected = false,
	disabled = false,
	onClick,
}) => {
	return (
		<Box
			component='button'
			type='button'
			disabled={disabled}
			onClick={onClick}
			sx={{
				height: '32px',
				minWidth: '56px',
				px: '16px',
				borderRadius: '12px',
				border: '1px solid #E2E2E2',
				backgroundColor: selected ? '#5222D01A' : '#FFFFFF',
				color: COLORS.black,
				fontSize: '14px',
				fontWeight: 400,
				cursor: disabled ? 'default' : 'pointer',
				opacity: disabled ? 0.5 : 1,
			}}
		>
			{label}
		</Box>
	)
}

interface CarouselPlaceOptionProps {
	label: string
	selected?: boolean
	disabled?: boolean
	onClick?: () => void
}

const CarouselPlaceOption: FC<CarouselPlaceOptionProps> = ({
	label,
	selected = false,
	disabled = false,
	onClick,
}) => {
	return (
		<Box
			component='button'
			type='button'
			disabled={disabled}
			onClick={onClick}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '10px',
				width: 'fit-content',
				border: 'none',
				backgroundColor: 'transparent',
				p: 0,
				cursor: disabled ? 'default' : 'pointer',
				opacity: disabled ? 0.5 : 1,
				transition: 'transform 0.16s ease',
				'&:active': {
					transform: disabled ? 'none' : 'scale(0.97)',
				},
			}}
		>
			<Box
				sx={{
					width: '24px',
					height: '24px',
					borderRadius: '50%',
					border: `2px solid ${selected ? '#5222D0' : '#E1E1E1'}`,
					backgroundColor: '#FFFFFF',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					transition:
						'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
					boxShadow: selected ? '0 0 0 2px #5222D01A' : 'none',
				}}
			>
				<Box
					sx={{
						width: '14px',
						height: '14px',
						borderRadius: '50%',
						backgroundColor: '#5222D0',
						transform: selected ? 'scale(1)' : 'scale(0)',
						opacity: selected ? 1 : 0,
						transition: 'transform 0.2s ease, opacity 0.2s ease',
					}}
				/>
			</Box>
			<TypographyPrimary>{label}</TypographyPrimary>
		</Box>
	)
}

const MilkerAvatar: FC<{ photo: string | null; name: string }> = ({
	photo,
	name,
}) => {
	if (photo) {
		return (
			<Box
				component='img'
				src={photo}
				alt={name}
				sx={{
					width: '28px',
					height: '28px',
					borderRadius: '50%',
					objectFit: 'cover',
					flexShrink: 0,
				}}
			/>
		)
	}
	const initials = name
		.split(' ')
		.slice(0, 2)
		.map(w => w[0] ?? '')
		.join('')
		.toUpperCase()
	return (
		<Box
			sx={{
				width: '28px',
				height: '28px',
				borderRadius: '50%',
				background: COLORS.background,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				color: COLORS.main,
				fontWeight: 600,
				fontSize: '11px',
				flexShrink: 0,
			}}
		>
			{initials}
		</Box>
	)
}

interface PlaceOptionTooltipProps {
	occupied: boolean
	children: ReactNode
}

const PlaceOptionTooltip: FC<PlaceOptionTooltipProps> = ({
	occupied,
	children,
}) => (
	<Tooltip
		title={occupied ? 'Место уже назначено' : ''}
		disableHoverListener={!occupied}
		disableFocusListener={!occupied}
		disableTouchListener={!occupied}
		placement='bottom-start'
		PopperProps={{
			modifiers: [
				{
					name: 'offset',
					options: {
						offset: [0, 2],
					},
				},
			],
		}}
		componentsProps={{
			tooltip: {
				sx: {
					backgroundColor: '#FFFFFF',
					color: '#7F7F84',
					borderRadius: '12px',
					padding: '8px 12px',
					fontSize: '14px',
					fontWeight: 400,
					lineHeight: 1.2,
					boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
				},
			},
		}}
	>
		<Box sx={{ width: 'fit-content' }}>{children}</Box>
	</Tooltip>
)

const selectSx = {
	width: '100%',
	'& .MuiOutlinedInput-root': {
		fontSize: '14px',
		borderRadius: '8px',
		height: '48px',
		'&.Mui-focused fieldset': {
			border: `1px solid ${COLORS.main}`,
		},
		'&:hover fieldset': {
			border: `1px solid ${COLORS.hoverInputBorder}`,
		},
	},
}

export const AssignShiftDrawer: FC<AssignShiftDrawerProps> = ({
	open,
	onClose,
	initialValues,
}) => {
	const { data: milkers = [] } = useGetMilkersInfo()
	const { data: parlorNames } = useGetMilkingParlorNames()
	const queryClient = useQueryClient()
	const dispatch = useAppDispatch()
	const [milkerId, setMilkerId] = useState<number | ''>('')
	const [workDate, setWorkDate] = useState<Moment | null>(null)
	const [parlorId, setParlorId] = useState<number | null>(null)
	const [period, setPeriod] = useState<ShiftOptions | null>(null)
	const [selectedPlaceIds, setSelectedPlaceIds] = useState<number[]>([])

	const parlors = parlorNames?.items ?? []
	const hasSingleParlor = parlors.length === 1
	const isDateEnabled = milkerId !== ''
	const isParlorEnabled = Boolean(workDate)
	const shouldShowParlorBlock = parlors.length > 1

	const selectedWorkDate = workDate?.format('YYYY-MM-DD')

	const selectedMilker = milkers.find(m => m.id === milkerId) ?? null
	const { data: milkingSchedulesData } = useQuery({
		queryKey: ['milking-schedules', parlorId, selectedWorkDate ?? ''],
		queryFn: () =>
			MilkingProcessService.getMilkingSchedules({
				parlorId: parlorId ?? undefined,
				...(selectedWorkDate ? { workDate: selectedWorkDate } : {}),
			}),
		enabled: Boolean(parlorId),
		staleTime: 15000,
	})
	const { data: milkerSchedulesData } = useQuery({
		queryKey: ['milking-schedules', 'milker', milkerId, selectedWorkDate ?? ''],
		queryFn: () =>
			MilkingProcessService.getMilkingSchedules({
				milkerId: milkerId === '' ? undefined : milkerId,
				...(selectedWorkDate ? { workDate: selectedWorkDate } : {}),
			}),
		enabled: Boolean(milkerId !== '' && selectedWorkDate),
		staleTime: 15000,
	})
	const milkingSchedules = milkingSchedulesData?.items ?? []
	const milkerSchedules = milkerSchedulesData?.items ?? []
	const {
		availablePeriods: availableParlorPeriods,
		isLoading: isShiftsLoading,
	} = useParlorAvailableShiftPeriods(parlorId)
	const shiftOptions = useMemo(
		() =>
			SHIFT_PERIOD_ORDER.map(shiftPeriod => ({
				value: shiftPeriod,
				title: SHIFT_PERIOD_LABELS[shiftPeriod],
				disabled: isShiftsLoading || !availableParlorPeriods.has(shiftPeriod),
			})),
		[availableParlorPeriods, isShiftsLoading],
	)
	const isSelectedShiftAvailable = period
		? availableParlorPeriods.has(period)
		: false
	const { data: parlorZonesData, isLoading: isParlorZonesLoading } =
		useGetParlorZones(parlorId)
	const parlorZones = useMemo(
		() =>
			[...(parlorZonesData?.items ?? [])].sort(
				(a, b) => a.milking_unit_num - b.milking_unit_num,
			),
		[parlorZonesData?.items],
	)
	const isCarouselLayout =
		parlorZones.length > 0 && parlorZones.every(zone => zone.type === 'post')
	const splitIndex = Math.ceil(parlorZones.length / 2)
	const leftColumnZones = parlorZones.slice(0, splitIndex)
	const rightColumnZones = parlorZones.slice(splitIndex)
	const isShiftEnabled = Boolean(parlorId)
	const isPlaceEnabled = Boolean(period) && isSelectedShiftAvailable
	const occupiedPlaceIds = useMemo(() => {
		if (!period || !selectedWorkDate) return new Set<number>()

		return new Set(
			milkingSchedules
				.filter(schedule => schedule.period === period)
				.flatMap(schedule => schedule.zones),
		)
	}, [milkingSchedules, period])
	const selectedPlaceLabels = useMemo(
		() =>
			selectedPlaceIds.map(zoneId => {
				const zone = parlorZones.find(item => item.id === zoneId)
				return zone?.name ?? String(zoneId)
			}),
		[parlorZones, selectedPlaceIds],
	)
	const milkerConflictSchedule = useMemo(() => {
		if (!period || !selectedWorkDate || milkerId === '') return null

		return (
			milkerSchedules.find(
				schedule => schedule.period === period && schedule.parlor !== parlorId,
			) ?? null
		)
	}, [milkerSchedules, milkerId, parlorId, period, selectedWorkDate])
	const hasMilkerPeriodConflict = Boolean(milkerConflictSchedule)
	const canSave =
		milkerId !== '' &&
		Boolean(workDate) &&
		Boolean(parlorId) &&
		isSelectedShiftAvailable &&
		!hasMilkerPeriodConflict &&
		selectedPlaceIds.length > 0
	const { mutate: createMilkingSchedule, isPending: isSaving } = useMutation({
		mutationFn: (payload: {
			milker: number
			parlor: number
			work_date: string
			shift_period: ShiftOptions
			zones: number[]
		}) => MilkingProcessService.createMilkingSchedule(payload),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['milking-schedules-grid'] }),
				queryClient.invalidateQueries({ queryKey: ['milking-schedules'] }),
			])
			handleClose()
			dispatch(
				notifyUser({
					title: 'Смена назначена',
					description: '',
					isError: false,
				}),
			)
		},
		onError: () => {
			dispatch(
				notifyUser({
					title: 'Произошла ошибка',
					description: 'Попробуйте снова.',
					isError: true,
				}),
			)
		},
	})

	useEffect(() => {
		if (!open) return
		if (!workDate || !hasSingleParlor) return
		setParlorId(parlors[0].id)
	}, [open, workDate, hasSingleParlor, parlors])

	useEffect(() => {
		if (!open || !initialValues) return

		setMilkerId(initialValues.milkerId ?? '')
		setWorkDate(
			typeof initialValues.workDate === 'number'
				? moment(initialValues.workDate)
				: null,
		)
		setParlorId(initialValues.parlorId ?? null)
		setPeriod(initialValues.period ?? null)
		setSelectedPlaceIds([])
	}, [open, initialValues])

	const handleClose = () => {
		setMilkerId('')
		setWorkDate(null)
		setParlorId(null)
		setPeriod(null)
		setSelectedPlaceIds([])
		onClose()
	}

	const handleMilkerChange = (value: number | '') => {
		setMilkerId(value)
		setWorkDate(null)
		setParlorId(null)
		setPeriod(null)
		setSelectedPlaceIds([])
	}

	const handleDateChange = (date: Moment | null) => {
		setWorkDate(date)
		setPeriod(null)
		setSelectedPlaceIds([])
		if (!hasSingleParlor) {
			setParlorId(null)
		}
	}

	const handleParlorChange = (id: number) => {
		setParlorId(id)
		setPeriod(null)
		setSelectedPlaceIds([])
	}

	const handleShiftChange = (value: ShiftOptions) => {
		if (isShiftsLoading || !availableParlorPeriods.has(value)) return

		setPeriod(value)
		setSelectedPlaceIds([])
	}

	const handlePlaceToggle = (zoneId: number) => {
		if (occupiedPlaceIds.has(zoneId)) return

		setSelectedPlaceIds(prev => {
			if (isCarouselLayout) {
				return prev[0] === zoneId ? [] : [zoneId]
			}
			return prev.includes(zoneId)
				? prev.filter(item => item !== zoneId)
				: [...prev, zoneId]
		})
	}

	const handleSave = () => {
		if (
			milkerId === '' ||
			!workDate ||
			!parlorId ||
			!period ||
			hasMilkerPeriodConflict ||
			selectedPlaceIds.length === 0
		) {
			return
		}

		createMilkingSchedule({
			milker: milkerId,
			parlor: parlorId,
			work_date: workDate.format('YYYY-MM-DD'),
			shift_period: period,
			zones: selectedPlaceIds,
		})
	}

	useEffect(() => {
		if (selectedPlaceIds.length === 0) return
		setSelectedPlaceIds(prev => {
			const next = prev.filter(zoneId => !occupiedPlaceIds.has(zoneId))
			return next.length === prev.length ? prev : next
		})
	}, [occupiedPlaceIds])

	useEffect(() => {
		if (!period) return
		if (isShiftsLoading || !availableParlorPeriods.has(period)) {
			setPeriod(null)
			setSelectedPlaceIds([])
		}
	}, [availableParlorPeriods, isShiftsLoading, period])

	useEffect(() => {
		if (selectedPlaceIds.length === 0) return
		const validZoneIds = new Set(parlorZones.map(zone => zone.id))
		setSelectedPlaceIds(prev => {
			const next = prev.filter(zoneId => validZoneIds.has(zoneId))
			return next.length === prev.length ? prev : next
		})
	}, [parlorZones])

	return (
		<Drawer
			open={open}
			onClose={handleClose}
			title='Назначить смену'
			anchor='right'
		>
			<Stack
				direction='column'
				gap='16px'
				margin='0px 24px'
				sx={{ minHeight: '100%' }}
			>
				<Stack direction='column' gap='8px'>
					<TypographyPrimary14Bold>Исполнитель</TypographyPrimary14Bold>
					<Box sx={selectSx}>
						<FormControl sx={{ width: '100%' }}>
							<Select
								displayEmpty
								value={milkerId}
								IconComponent={Arrow}
								sx={{
									'& .MuiSelect-icon svg path': {
										stroke: '#7F7F84',
									},
								}}
								onChange={(e: SelectChangeEvent<number | ''>) =>
									handleMilkerChange(
										e.target.value === '' ? '' : Number(e.target.value),
									)
								}
								renderValue={value =>
									value === '' || !selectedMilker ? (
										<TypographyPrimary sx={{ color: '#A0A0A0' }}>
											Выберите исполнителя
										</TypographyPrimary>
									) : (
										<Stack direction='row' alignItems='center' gap='8px'>
											<MilkerAvatar
												photo={selectedMilker.photo}
												name={selectedMilker.full_name}
											/>
											<TypographyPrimary>
												{selectedMilker.full_name}
											</TypographyPrimary>
										</Stack>
									)
								}
								MenuProps={{
									PaperProps: { style: { maxWidth: 425, maxHeight: 300 } },
								}}
							>
								{milkers.map((milker: IMilker) => (
									<MenuItem key={milker.id} value={milker.id}>
										<Stack direction='row' alignItems='center' gap='8px'>
											<MilkerAvatar
												photo={milker.photo}
												name={milker.full_name}
											/>
											<TypographyPrimary>{milker.full_name}</TypographyPrimary>
										</Stack>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
				</Stack>

				<Stack direction='column' gap='8px'>
					<TypographyPrimary14Bold>Дата</TypographyPrimary14Bold>
					<Box
						sx={{
							opacity: isDateEnabled ? 1 : 0.5,
							pointerEvents: isDateEnabled ? 'auto' : 'none',
						}}
					>
						<DatePicker
							key={`assign-shift-date-${workDate?.valueOf() ?? 'empty'}`}
							value={workDate?.valueOf()}
							onChange={handleDateChange}
							placeholder='Выберите дату'
							restrictionType={DateRestriction.FUTURE}
						/>
					</Box>
				</Stack>

				{shouldShowParlorBlock && (
					<Stack direction='column' gap='8px'>
						<TypographyPrimary14Bold>Доильный зал</TypographyPrimary14Bold>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '8px',
								opacity: isParlorEnabled ? 1 : 0.5,
								pointerEvents: isParlorEnabled ? 'auto' : 'none',
							}}
						>
							{parlors.map(parlor => (
								<OptionChip
									key={parlor.id}
									label={parlor.name}
									selected={parlor.id === parlorId}
									onClick={() => handleParlorChange(parlor.id)}
								/>
							))}
						</Box>
					</Stack>
				)}

				<Stack direction='column' gap='8px'>
					<TypographyPrimary14Bold>Смена</TypographyPrimary14Bold>
					{isShiftEnabled ? (
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '8px',
							}}
						>
							{shiftOptions.map(shift => (
								<OptionChip
									key={shift.value}
									label={shift.title}
									selected={period === shift.value}
									disabled={shift.disabled}
									onClick={() => handleShiftChange(shift.value)}
								/>
							))}
						</Box>
					) : (
						<TypographyPrimary sx={{ color: '#A0A0A0' }}>
							Выберите доильный зал, чтобы выбрать смену.
						</TypographyPrimary>
					)}
					{hasMilkerPeriodConflict && (
						<TypographyPrimary sx={{ color: '#C62828' }}>
							У этого дояра уже есть смена в зале{' '}
							{milkerConflictSchedule?.parlor_name} на это время.
						</TypographyPrimary>
					)}
				</Stack>

				<Stack direction='column' gap='8px'>
					<TypographyPrimary14Bold>Доильное место</TypographyPrimary14Bold>
					{isPlaceEnabled ? (
						isParlorZonesLoading ? (
							<TypographyPrimary sx={{ color: '#A0A0A0' }}>
								Загрузка мест...
							</TypographyPrimary>
						) : parlorZones.length === 0 ? (
							<TypographyPrimary sx={{ color: '#A0A0A0' }}>
								Для выбранного зала места не настроены.
							</TypographyPrimary>
						) : (
							<Stack direction='column' gap='8px'>
								{isCarouselLayout ? (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: '10px',
										}}
									>
										{parlorZones.map(zone => (
											<PlaceOptionTooltip
												key={zone.id}
												occupied={occupiedPlaceIds.has(zone.id)}
											>
												<CarouselPlaceOption
													label={zone.name}
													selected={selectedPlaceIds.includes(zone.id)}
													disabled={occupiedPlaceIds.has(zone.id)}
													onClick={() => handlePlaceToggle(zone.id)}
												/>
											</PlaceOptionTooltip>
										))}
									</Box>
								) : (
									<Stack direction='row' alignItems='flex-start' gap='24px'>
										<Stack direction='column' gap='8px'>
											{leftColumnZones.map(zone => (
												<PlaceOptionTooltip
													key={zone.id}
													occupied={occupiedPlaceIds.has(zone.id)}
												>
													<PlaceChip
														label={zone.name}
														selected={selectedPlaceIds.includes(zone.id)}
														disabled={occupiedPlaceIds.has(zone.id)}
														onClick={() => handlePlaceToggle(zone.id)}
													/>
												</PlaceOptionTooltip>
											))}
										</Stack>
										<Stack direction='column' gap='8px'>
											{rightColumnZones.map(zone => (
												<PlaceOptionTooltip
													key={zone.id}
													occupied={occupiedPlaceIds.has(zone.id)}
												>
													<PlaceChip
														label={zone.name}
														selected={selectedPlaceIds.includes(zone.id)}
														disabled={occupiedPlaceIds.has(zone.id)}
														onClick={() => handlePlaceToggle(zone.id)}
													/>
												</PlaceOptionTooltip>
											))}
										</Stack>
										<Box sx={{ pt: '4px', ml: '60px', minWidth: '140px' }}>
											<TypographyPrimary sx={{ mb: '8px' }}>
												Выбранные места:
											</TypographyPrimary>
											<TypographyPrimary>
												{selectedPlaceLabels.length
													? selectedPlaceLabels.join(', ')
													: '-'}
											</TypographyPrimary>
										</Box>
									</Stack>
								)}
							</Stack>
						)
					) : (
						<TypographyPrimary sx={{ color: '#A0A0A0' }}>
							Выберите доильный зал и смену, чтобы выбрать место.
						</TypographyPrimary>
					)}
				</Stack>

				<Box
					sx={{
						mt: 'auto',
						pb: '24px',
						pt: '8px',
						position: 'sticky',
						bottom: 0,
						zIndex: 3,
						backgroundColor: 'transparent',
					}}
				>
					<ButtonUI
						disabled={!canSave || isSaving}
						onClick={handleSave}
						sx={{ height: '56px', width: '100%' }}
					>
						Сохранить
					</ButtonUI>
				</Box>
			</Stack>
		</Drawer>
	)
}
